import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const fmtDollarAxis = (v) => v >= 1000 ? `$${(v / 1000).toLocaleString()}k` : `$${v.toLocaleString()}`;
import ChartCard from '../components/ChartCard';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatPercent, formatNumber, getDatePreset } from '../utils/formatters';
import DateRangeFilter from '../components/DateRangeFilter';
import KPICard from '../components/KPICard';
import ExportButton from '../components/ExportButton';

// The three buckets COGS is made of, spelled out wherever COGS is shown on this
// page. Paid Order = non-break product orders + everything on a break (Break
// Products and Product Additions) + imported auction COGS; Freebie = products
// recorded in the Export Skunks & Trades flow; Giveaway = orders of the
// giveaway listing(s), followed by how many giveaways that was.
function giveawayLabel(breakdown) {
  return `${formatCurrency(breakdown.giveaway || 0)} (${Number(breakdown.giveawayCount || 0).toLocaleString('en-US')})`;
}

function cogsInfo(breakdown) {
  if (!breakdown) return 'Product COGS from orders plus break COGS';
  return [
    `Paid Order COGS: ${formatCurrency(breakdown.paidOrder || 0)}`,
    `Freebie COGS: ${formatCurrency(breakdown.freebie || 0)}`,
    `Giveaway COGS: ${giveawayLabel(breakdown)}`,
  ].join(' | ');
}

// Recharts tooltip for the Net Sales Breakdown pie. Behaves like the default
// one, except the COGS slice also lists the three buckets behind it.
function CogsAwareTooltip({ active, payload, breakdown }) {
  if (!active || !payload?.length) return null;
  const slice = payload[0];
  const isCogs = slice.name === 'COGS' && breakdown;
  return (
    <div style={{
      background: 'white', border: '1px solid var(--border)', borderRadius: 6,
      padding: '8px 10px', fontSize: 12, boxShadow: 'var(--shadow-lg)', lineHeight: 1.6,
    }}>
      <div style={{ fontWeight: 600 }}>{slice.name}: {formatCurrency(slice.value)}</div>
      {isCogs && (
        <div style={{ color: 'var(--text-secondary)' }}>
          <div>Paid Order COGS: {formatCurrency(breakdown.paidOrder || 0)}</div>
          <div>Freebie COGS: {formatCurrency(breakdown.freebie || 0)}</div>
          <div>Giveaway COGS: {giveawayLabel(breakdown)}</div>
        </div>
      )}
    </div>
  );
}

const BREAKDOWN_COLORS = {
  'Net Profit': '#22c55e',
  'COGS': '#ef4444',
  'Ad Spend': '#f59e0b',
  'TikTok Fees': '#3b82f6',
  'Affiliate Commission': '#8b5cf6',
  'Shipping Costs': '#64748b',
};

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialState = useMemo(() => {
    const sd = searchParams.get('startDate');
    const ed = searchParams.get('endDate');
    if (sd && ed) return { preset: 'custom', dateRange: { startDate: sd, endDate: ed } };
    return { preset: 'today', dateRange: getDatePreset('today') };
  }, []);
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [mtd, setMtd] = useState(null);
  const [dateRange, setDateRange] = useState(initialState.dateRange);
  const isBuyer = user?.role === 'buyer';
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [groupBy, setGroupBy] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [visibleLines, setVisibleLines] = useState({ netSales: true, netProfit: true, profitMargin: true });
  const [hourRange, setHourRange] = useState({ start: 0, end: 23 });
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        api.get('/api/dashboard/kpis', { params: dateRange }),
        api.get('/api/dashboard/charts', { params: { ...dateRange, groupBy } }),
        api.get('/api/dashboard/mtd-comparison')
      ];
      if (groupBy === 'hourly') {
        requests.push(api.get('/api/dashboard/ads-hourly', { params: { date: dateRange.startDate } }));
      }
      const results = await Promise.all(requests);
      setKpis(results[0].data);
      const chartData = results[1].data;
      // Merge hourly ads data into chart trend if available
      if (groupBy === 'hourly' && results[3]?.data?.hourlyTrend) {
        const adsHourly = results[3].data.hourlyTrend;
        chartData.dailyTrend = chartData.dailyTrend.map((point, i) => ({
          ...point,
          adSpend: (point.adSpend || 0) + (adsHourly[i]?.adSpend || 0),
          adsAttributedGmv: (point.adsAttributedGmv || 0) + (adsHourly[i]?.attributedGmv || 0),
          roas: ((adsHourly[i]?.adSpend || 0) > 0)
            ? Math.round((adsHourly[i]?.attributedGmv || 0) / (adsHourly[i]?.adSpend || 0) * 100) / 100
            : point.roas,
        }));
      }
      setCharts(chartData);
      setMtd(results[2].data);
    } catch (err) {
      console.error('Financials fetch error:', err);
      setError('Failed to load financial data. Please try again.');
    }
    setLoading(false);
  }, [dateRange, groupBy]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);
  const filteredTrend = useMemo(() => {
    if (!charts?.dailyTrend || groupBy !== 'hourly') return charts?.dailyTrend;
    return charts.dailyTrend.filter((_, i) => i >= hourRange.start && i <= hourRange.end);
  }, [charts?.dailyTrend, groupBy, hourRange]);

  const groupLabel = groupBy === 'hourly' ? 'Hourly' : groupBy === 'weekly' ? 'Weekly' : groupBy === 'monthly' ? 'Monthly' : 'Daily';
  const formatXLabel = (val) => {
    if (groupBy === 'hourly') return val; // Already formatted as "9 AM" etc.
    if (groupBy === 'monthly') { const [y, m] = val.split('-'); return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); }
    if (groupBy === 'weekly') { const d = new Date(val + 'T12:00:00'); return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`; }
    return val;
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Financials
          <button onClick={async () => {
              if (syncing) return;
              setSyncing(true);
              try {
                const { data } = await api.post('/api/dashboard/sync');
                setSyncMsg({ text: data.message, error: !data.ok });
                if (data.details) console.log('Sync details:', JSON.stringify(data.details, null, 2));
                await fetchData();
                setTimeout(() => setSyncMsg(null), data.ok ? 5000 : 15000);
              } catch (err) {
                console.error('Sync error:', err);
                setSyncMsg({ text: 'Sync failed — check console', error: true });
                setTimeout(() => setSyncMsg(null), 10000);
              }
              setSyncing(false);
            }} className="refresh-btn" title="Sync latest data from TikTok"
            aria-label={syncing ? 'Syncing data...' : 'Sync latest data from TikTok'}
            disabled={syncing}
            style={{ background: 'none', border: 'none', cursor: syncing ? 'wait' : 'pointer', padding: '4px', display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={syncing ? { animation: 'spin 1s linear infinite' } : {}}>
              <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
          {syncMsg && <span aria-live="polite" style={{ fontSize: '12px', fontWeight: 400, color: syncMsg.error ? 'var(--danger)' : 'var(--text-secondary)' }}>{syncMsg.text}</span>}
        </h2>
        <ExportButton endpoint="/api/dashboard/export" params={dateRange} filename="dashboard-export.csv" />
      </div>

      <DateRangeFilter onChange={setDateRange} initial={initialState.preset} initialCustom={initialState.preset === 'custom' ? initialState.dateRange : undefined} />

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><div className="spinner" /></div>}

      {!loading && error && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <p style={{ color: 'var(--danger)', marginBottom: '12px' }}>{error}</p>
          <button className="btn btn-primary" onClick={() => { setError(null); fetchData(); }}>Retry</button>
        </div>
      )}

      {!loading && !error && !kpis && <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-light)' }}>No data available</div>}

      {!loading && kpis && <>
      {/* At a Glance */}
      {!isBuyer && kpis.profitability && (
        <div className="kpi-section">
          <div className="kpi-section-title">At a Glance</div>
          <div className="kpi-grid at-a-glance-grid">
            <KPICard label="Net Sales" value={formatCurrency(kpis.revenue.netSales)} info="Gross Sales − Discounts − Refunds" />
            <KPICard label="Net Profit" value={formatCurrency(kpis.profitability.netProfit)} info="Gross Profit − Ad Spend − TikTok Fees − Shipping" />
            <KPICard label="Net Profit Margin" value={formatPercent(kpis.profitability.profitMargin)} info="Net Profit ÷ Net Sales × 100" />
          </div>
        </div>
      )}

      {/* Revenue KPIs */}
      <div className="kpi-section">
        <div className="kpi-section-title">Revenue</div>
        <div className="kpi-grid">
          <KPICard label="GMV (Total Sales)" value={formatCurrency(kpis.revenue.gmv)} lastUpdated={kpis.meta.shopLastUpdated}
            info={kpis.meta.pstGmv ? `TikTok (PST): ${formatCurrency(kpis.meta.pstGmv.gmv)} (${kpis.meta.pstGmv.orders} orders)` : undefined} />
          {!isBuyer && <KPICard label="Net Sales" value={formatCurrency(kpis.revenue.netSales)} info="GMV − Discounts − Refunds" />}
          <KPICard label="Orders" value={kpis.revenue.orderCount.toLocaleString()} />
        </div>
      </div>

      {/* Costs */}
      {!isBuyer && kpis.costs && (() => {
        const ns = kpis.revenue?.netSales;
        const pct = (v) => ns ? formatPercent((v / ns) * 100) : '--';
        return (
        <div className="kpi-section">
          <div className="kpi-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span>Costs</span>
            <span style={{ fontSize: '12px', fontWeight: 500, fontStyle: 'italic', color: 'var(--text-secondary)' }}>% of Net Sales</span>
          </div>
          <div className="kpi-grid">
            <KPICard label="COGS" value={formatCurrency(kpis.costs.cogs)} secondary={pct(kpis.costs.cogs)}
              info={cogsInfo(kpis.costs.cogsBreakdown)} />
            <KPICard label="Ad Spend" value={formatCurrency(kpis.costs.adSpend)} secondary={pct(kpis.costs.adSpend)} />
            <KPICard label="TikTok Fees" value={formatCurrency(kpis.costs.tiktokFees || 0)}
              secondary={pct(kpis.costs.tiktokFees || 0)}
              info={kpis.costs.tiktokFeesBreakdown ? [
                `Referral Fees: ${formatCurrency(kpis.costs.tiktokFeesBreakdown.referralFees || 0)}`,
                `Platform Commission: ${formatCurrency(kpis.costs.tiktokFeesBreakdown.platformCommission || 0)}`,
                `Transaction Fees: ${formatCurrency(kpis.costs.tiktokFeesBreakdown.transactionFees || 0)}`,
                kpis.costs.tiktokFeesBreakdown.otherFees > 0 ? `Other Fees: ${formatCurrency(kpis.costs.tiktokFeesBreakdown.otherFees)}` : null,
                kpis.costs.tiktokFeesBreakdown.estimated > 0 ? `Estimated (unsettled): ${formatCurrency(kpis.costs.tiktokFeesBreakdown.estimated)}` : null,
              ].filter(Boolean).join(' | ') : 'Referral fees, platform commission, transaction fees'} />
            <KPICard label="Affiliate Commission" value={formatCurrency(kpis.costs.affiliateCommission || 0)}
              secondary={pct(kpis.costs.affiliateCommission || 0)}
              info="Commission paid to TikTok creators/affiliates" />
            <KPICard label="Seller Shipping Costs" value={formatCurrency(kpis.costs.sellerShipping)}
              secondary={pct(kpis.costs.sellerShipping)}
              info="Total shipping cost minus customer-paid shipping" />
          </div>
        </div>
        );
      })()}

      {/* Profitability */}
      {!isBuyer && kpis.profitability && (
        <div className="kpi-section">
          <div className="kpi-section-title">Profitability</div>
          <div className="kpi-grid">
            <KPICard label="Gross Profit" value={formatCurrency(kpis.profitability.grossProfit)} info="Net Sales − COGS" />
            <KPICard label="Net Profit" value={formatCurrency(kpis.profitability.netProfit)} info="Gross Profit − Ad Spend − TikTok Fees − Shipping" />
            <KPICard label="Net Profit Margin" value={formatPercent(kpis.profitability.profitMargin)} info="Net Profit ÷ Net Sales × 100" />
          </div>
        </div>
      )}

      {/* Ads Performance */}
      {!isBuyer && kpis.adsPerformance && (
        <div className="kpi-section">
          <div className="kpi-section-title">Ads Performance</div>
          <div className="kpi-grid">
            <KPICard label="Ad Spend" value={formatCurrency(kpis.adsPerformance.adSpend)} lastUpdated={kpis.meta.adsLastUpdated} />
            <KPICard label="Ads Attributed GMV" value={formatCurrency(kpis.adsPerformance.attributedGmv)} info="Revenue attributed to ads using TikTok's 24-hour attribution window" />
            <KPICard label="Total GMV (Shop)" value={formatCurrency(kpis.adsPerformance.totalGmv)} />
            <KPICard label="ROAS" value={kpis.adsPerformance.roas?.toFixed(2) + 'x'} info="Ads Attributed GMV ÷ Ad Spend" />
            <KPICard label="Organic GMV" value={formatCurrency(kpis.adsPerformance.organicGmv)} info="Total GMV − Ads Attributed GMV" />
          </div>
        </div>
      )}

      {/* Live Performance */}
      {!isBuyer && kpis.livePerformance && (
        <div className="kpi-section">
          <div className="kpi-section-title">Live Performance</div>
          <div className="kpi-grid">
            <KPICard label="Live Days" value={kpis.livePerformance.liveDays} info="Distinct days a break landed in completed or pending" />
            <KPICard label="Total Breaks" value={kpis.livePerformance.totalBreaks ?? 0} info="Breaks in completed or pending status" />
            <KPICard label="Live Day GMV" value={formatCurrency(kpis.livePerformance.liveDayGmv)} info="Total GMV from orders placed on live days" />
            <KPICard label="Avg GMV per Live Day" value={formatCurrency(kpis.livePerformance.avgGmvPerLiveDay)} info="Live Day GMV ÷ Live Days" />
          </div>
        </div>
      )}

      {/* Charts */}
      {charts && (
        <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>Group by:</span>
          {['hourly', 'daily', 'weekly', 'monthly'].map(g => (
            <button key={g} className={`date-preset ${groupBy === g ? 'active' : ''}`}
              onClick={() => setGroupBy(g)} style={{ padding: '4px 12px', fontSize: '12px' }}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
          {groupBy === 'hourly' && (
            <>
              <span style={{ fontSize: '12px', color: 'var(--text-light)', marginLeft: '8px' }}>from</span>
              <select value={hourRange.start} onChange={e => setHourRange(prev => ({ ...prev, start: parseInt(e.target.value) }))}
                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border)' }}>
                {HOUR_LABELS.map((label, i) => <option key={i} value={i}>{label}</option>)}
              </select>
              <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>to</span>
              <select value={hourRange.end} onChange={e => setHourRange(prev => ({ ...prev, end: parseInt(e.target.value) }))}
                style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border)' }}>
                {HOUR_LABELS.map((label, i) => <option key={i} value={i}>{label}</option>)}
              </select>
            </>
          )}
        </div>
        <div className="charts-grid">
          <ChartCard title={`${groupLabel} Sales & Profitability`} style={{ gridColumn: '1 / -1' }}>
            {/* Doubles as this chart's key — ChartCard reads `.chart-key` when
                drawing the shared PNG, since the chart has no recharts
                <Legend> of its own. */}
            <div className="chart-key" style={{ display: 'flex', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
              {[
                { key: 'netSales', label: 'Net Sales', color: '#3182ce' },
                { key: 'netProfit', label: 'Net Profit', color: '#38a169' },
                { key: 'profitMargin', label: 'Net Profit Margin', color: '#d69e2e' },
              ].map(({ key, label, color }) => (
                <button key={key}
                  onClick={() => setVisibleLines(prev => ({ ...prev, [key]: !prev[key] }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '4px 10px', fontSize: '12px', borderRadius: '4px', cursor: 'pointer',
                    border: '1px solid var(--border)',
                    background: visibleLines[key] ? 'var(--bg-card)' : 'var(--bg)',
                    opacity: visibleLines[key] ? 1 : 0.4,
                  }}>
                  <span style={{ width: '12px', height: '3px', borderRadius: '2px', background: color, display: 'inline-block' }} />
                  {label}
                </button>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={filteredTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={formatXLabel} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtDollarAxis} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => v.toFixed(0) + '%'} />
                <Tooltip formatter={(v, name) => name === 'Net Profit Margin' ? v.toFixed(1) + '%' : formatCurrency(v)} />
                {visibleLines.netSales && <Line yAxisId="left" type="monotone" dataKey="netSales" name="Net Sales" stroke="#3182ce" strokeWidth={2} dot={false} />}
                {visibleLines.netProfit && <Line yAxisId="left" type="monotone" dataKey="netProfit" name="Net Profit" stroke="#38a169" strokeWidth={2} dot={false} />}
                {visibleLines.profitMargin && <Line yAxisId="right" type="monotone" dataKey="profitMargin" name="Net Profit Margin" stroke="#d69e2e" strokeWidth={2} dot={false} />}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title={`${groupLabel} ROAS Trend`}>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={filteredTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={formatXLabel} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v.toFixed(1) + 'x'} />
                <Tooltip formatter={(v) => v.toFixed(2) + 'x'} />
                <Line type="monotone" dataKey="roas" name="ROAS" stroke="#d69e2e" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Net Sales Breakdown">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                {/* Percentage radii rather than fixed pixels so the donut fills whatever
                    box it is given — the card, the expanded landscape view, and the
                    fixed-size canvas the share button renders into. */}
                <Pie data={charts.costBreakdown} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius="78%" innerRadius="32%"
                  label={({ name, percent }) => percent > 0.03 ? `${(percent * 100).toFixed(0)}%` : ''} labelLine={false}
                  paddingAngle={1}>
                  {charts.costBreakdown.map((entry, i) => <Cell key={i} fill={BREAKDOWN_COLORS[entry.name] || '#94a3b8'} />)}
                </Pie>
                <Tooltip content={<CogsAwareTooltip breakdown={charts.cogsBreakdown} />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        </div>
      )}

      {/* MTD Comparison */}
      {mtd && !isBuyer && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: '16px' }}>Month-to-Date vs Prior Month</div>
          <div className="mtd-grid">
            {[
              { label: 'GMV', key: 'gmv', format: formatCurrency },
              { label: 'Net Profit', key: 'netProfit', format: formatCurrency },
              { label: 'Profit Margin', key: 'profitMargin', format: v => (v ?? 0).toFixed(1) + '%' },
              { label: 'Live Days', key: 'liveDays', format: v => (v ?? 0) },
              { label: 'Total Breaks', key: 'totalBreaks', format: v => (v ?? 0) }
            ].map(({ label, key, format }) => (
              <div key={key} className="mtd-item">
                <div className="kpi-label">{label}</div>
                <div className="mtd-current">{format(mtd.current[key])}</div>
                <div className="mtd-previous">Prior: {format(mtd.previous[key])}</div>
                {mtd.changes[key] !== null && (
                  <div className={`kpi-change ${mtd.changes[key] >= 0 ? 'positive' : 'negative'}`}>
                    {mtd.changes[key] >= 0 ? '+' : ''}{mtd.changes[key]?.toFixed(1)}%
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      </>}
    </div>
  );
}
